import { useEffect, useMemo, useRef, type MutableRefObject } from "react";
import { config } from "../config";
import { getConsumerClerkToken, getOperation } from "../services/api";
import type { IOperationResponse } from "../services/api";
import {
  openWidgetSse,
  type WidgetSseConnection,
} from "../utils/widgetSse";
import { getEvents, safeCapture } from "../analytics";

/** Statuses past which an operation no longer changes (SSE can stop). */
const TERMINAL_STATUSES = new Set([
  "completed",
  "failed",
  "cancelled",
  "canceled",
  "expired",
]);

export function isOperationNonTerminal(op: IOperationResponse): boolean {
  return !TERMINAL_STATUSES.has((op.status ?? "").toLowerCase());
}

export function hasNonTerminalOperations(ops: IOperationResponse[]): boolean {
  return ops.some(isOperationNonTerminal);
}

/** Apply an operation delta to a list (replace by id, otherwise prepend). */
export function upsertOperationInList(
  list: IOperationResponse[],
  op: IOperationResponse
): IOperationResponse[] {
  const idx = list.findIndex((o) => o.operationId === op.operationId);
  if (idx >= 0) {
    const next = [...list];
    next[idx] = op;
    return next;
  }
  return [op, ...list];
}

function closeSse(
  conn: WidgetSseConnection | null,
  connRef: MutableRefObject<WidgetSseConnection | null>
): void {
  if (!conn) return;
  conn.close();
  if (connRef.current === conn) {
    connRef.current = null;
  }
}

function parseSsePayload(raw: unknown): Record<string, unknown> | null {
  try {
    let data: unknown = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (typeof data === "string") {
      data = JSON.parse(data);
    }
    if (!data || typeof data !== "object") return null;
    return data as Record<string, unknown>;
  } catch {
    return null;
  }
}

export interface UseOperationSSEOptions {
  /** If set, only stream while this operation exists in the list and is non-terminal. */
  watchOperationId?: string;
}

/**
 * Keeps operation statuses live via Server-Sent Events.
 *
 * Uses fetch (not EventSource) so the Consumer Clerk Bearer token can be sent.
 */
export function useOperationSSE(
  linkId: string | null | undefined,
  operations: IOperationResponse[],
  onUpsert: (op: IOperationResponse) => void,
  onReconnect?: () => void,
  options?: UseOperationSSEOptions
): void {
  const watchOperationId = options?.watchOperationId;
  const connRef = useRef<WidgetSseConnection | null>(null);
  const isFirstOpenRef = useRef(true);

  const operationsRef = useRef(operations);
  const onUpsertRef = useRef(onUpsert);
  const onReconnectRef = useRef(onReconnect);
  operationsRef.current = operations;
  onUpsertRef.current = onUpsert;
  onReconnectRef.current = onReconnect;

  const hasActiveOps = useMemo(() => {
    if (!watchOperationId) {
      return hasNonTerminalOperations(operations);
    }
    const op = operations.find((o) => o.operationId === watchOperationId);
    return op ? isOperationNonTerminal(op) : false;
  }, [operations, watchOperationId]);

  useEffect(() => {
    if (!linkId || !hasActiveOps) {
      closeSse(connRef.current, connRef);
      return;
    }

    if (connRef.current) return;

    isFirstOpenRef.current = true;
    const url = `${config.API_BASE_URL}/widget/links/${linkId}/operations/sse`;

    const maybeClose = (): void => {
      const ops = operationsRef.current;
      if (watchOperationId) {
        const op = ops.find((o) => o.operationId === watchOperationId);
        if (!op || !isOperationNonTerminal(op)) {
          closeSse(connRef.current, connRef);
        }
        return;
      }
      if (!hasNonTerminalOperations(ops)) {
        closeSse(connRef.current, connRef);
      }
    };

    const conn = openWidgetSse({
      url,
      headers: { "x-link-id": linkId },
      getHeaders: async () => {
        const token = await getConsumerClerkToken();
        const headers: Record<string, string> = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        return headers;
      },
      onOpen: () => {
        if (!isFirstOpenRef.current) {
          if (watchOperationId && linkId) {
            void getOperation(linkId, watchOperationId, {
              raasSilentOffline: true,
            })
              .then((fetched) => onUpsertRef.current(fetched))
              .catch((err) =>
                console.error(
                  "[useOperationSSE] reconnect: failed to refresh watched operation",
                  err
                )
              );
          } else {
            onReconnectRef.current?.();
          }
        }
        isFirstOpenRef.current = false;
      },
      onMessage: (event) => {
        try {
          const data = parseSsePayload(event.data);
          if (!data) return;

          if (data.type === "heartbeat") {
            maybeClose();
            return;
          }

          const updated = data as unknown as IOperationResponse;
          if (!updated?.operationId) return;

          if (watchOperationId && updated.operationId !== watchOperationId) {
            maybeClose();
            return;
          }

          safeCapture(getEvents().OPERATION_STATUS_CHANGED, {
            operation_id: updated.operationId,
            status: updated.status,
            funding_status: updated.fundingStatus,
            quote_id: updated.quoteId,
            send_amount: updated.sendAmount,
            receive_amount: updated.receiveAmount,
          });

          onUpsertRef.current(updated);
          maybeClose();
        } catch (e) {
          console.error("[useOperationSSE] message parse error", e);
        }
      },
      onError: () => {
        console.warn("[useOperationSSE] connection issue (auto-retry)", linkId);
      },
    });
    connRef.current = conn;

    return () => {
      closeSse(conn, connRef);
    };
  }, [linkId, hasActiveOps, watchOperationId]);
}
